import { logger } from "./logger.js";
import type { ChatConfig } from "./types/config.js";
import type {
  ToolAdapter,
  ToolCallHandler,
  ToolDefinition,
  ToolManifest,
  YakToolset,
} from "./types/tools.js";

type ResolvedAdapter = {
  adapter: ToolAdapter;
  tools: ToolDefinition[];
};

/**
 * Compose client-side {@link ToolAdapter}s into ONE merged tool manifest and ONE
 * routed `onToolCall`. This is the single injection point for "available tools"
 * into the iframe: every adapter — client-side (GraphQL/REST/custom, run by your own
 * `execute` callback) or server-relayed ({@link createYakServerAdapter}) — contributes
 * its tools to the merged manifest and its executor to the routed handler. Because every
 * tool now flows through `onToolCall`, they all surface through `onToolCallComplete` /
 * `useYakToolEvent`.
 *
 * @example
 * ```tsx
 * const toolset = createYakToolset([
 *   createYakServerAdapter({ endpoint: "/api/yak" }),               // tRPC + custom server tools
 *   createGraphQLToolAdapter({ name: "shop", schema, execute: runShopQuery }),
 *   createRESTToolAdapter({ name: "billing", spec, execute: callBillingApi }),
 * ]);
 *
 * <YakProvider
 *   getConfig={async () => ({ routes, ...(await toolset.getConfig()) })}
 *   onToolCall={toolset.onToolCall}
 * />;
 * ```
 */
export function createYakToolset(adapters: ToolAdapter[]): YakToolset {
  let cache: ResolvedAdapter[] | null = null;

  async function resolve(force = false): Promise<ResolvedAdapter[]> {
    if (cache && !force) return cache;
    cache = await Promise.all(
      adapters.map(async (adapter) => ({ adapter, tools: await adapter.getTools() }))
    );
    return cache;
  }

  async function getConfig(): Promise<{ tools: ToolManifest }> {
    // Refresh on every config load — tools may depend on page/user.
    const resolved = await resolve(true);
    const tools: ToolDefinition[] = [];
    const seen = new Set<string>();

    for (const { adapter, tools: adapterTools } of resolved) {
      for (const tool of adapterTools) {
        if (seen.has(tool.name)) {
          logger.warn(
            `Duplicate tool name "${tool.name}"; keeping the first and ignoring the one from adapter "${adapter.id ?? "unknown"}".`
          );
          continue;
        }
        seen.add(tool.name);
        tools.push(tool);
      }
    }

    return {
      tools: {
        tools,
        sources: resolved.map(({ adapter, tools: adapterTools }, index) => ({
          id: adapter.id ?? `adapter-${index}`,
          count: adapterTools.length,
        })),
      },
    };
  }

  const onToolCall: ToolCallHandler = async (name, args) => {
    // Fast path: explicit ownership predicates need no resolution.
    for (const adapter of adapters) {
      if (adapter.ownsTool?.(name)) {
        return adapter.execute(name, args);
      }
    }
    // Otherwise route by the adapter's resolved tool names.
    const resolved = await resolve();
    for (const { adapter, tools } of resolved) {
      if (adapter.ownsTool) continue; // already consulted above
      if (tools.some((tool) => tool.name === name)) {
        return adapter.execute(name, args);
      }
    }
    throw new Error(`Unknown tool: ${name}`);
  };

  return { getConfig, onToolCall };
}

/** Config for {@link createYakServerAdapter}. */
export type YakServerAdapterConfig = {
  /**
   * Endpoint mounting `createYakHandler` / `createNextYakHandler`. GET returns the
   * tool manifest; POST `{ name, args }` executes a tool. Default: `"/api/yak"`.
   */
  endpoint?: string;
  /** Stable id for diagnostics. Default: `"server"`. */
  id?: string;
  /** Extra headers (e.g. auth) applied to both the GET manifest load and POST execution. */
  headers?: HeadersInit | (() => HeadersInit | Promise<HeadersInit>);
};

async function buildHeaders(
  source: YakServerAdapterConfig["headers"],
  base?: Record<string, string>
): Promise<Headers> {
  const headers = new Headers(base);
  const resolved = typeof source === "function" ? await source() : source;
  if (resolved) {
    for (const [key, value] of new Headers(resolved)) {
      headers.set(key, value);
    }
  }
  return headers;
}

/**
 * Bridge a server-side yak handler ({@link createYakHandler} /
 * `createNextYakHandler`, e.g. fronting the `@yak-io/trpc` adapter) into a
 * client-side {@link ToolAdapter}, so server-relayed tools merge into the same
 * manifest + `onToolCall` as browser-executed adapters.
 */
export function createYakServerAdapter(config: YakServerAdapterConfig = {}): ToolAdapter {
  const endpoint = config.endpoint ?? "/api/yak";

  return {
    id: config.id ?? "server",
    getTools: async () => {
      const res = await fetch(endpoint, { headers: await buildHeaders(config.headers) });
      if (!res.ok) {
        throw new Error(`Failed to load tools from ${endpoint} (${res.status})`);
      }
      const chatConfig = (await res.json()) as ChatConfig;
      return chatConfig.tools?.tools ?? [];
    },
    execute: async (name, args) => {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: await buildHeaders(config.headers, { "Content-Type": "application/json" }),
        body: JSON.stringify({ name, args }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        result?: unknown;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error ?? `Tool "${name}" failed (${res.status})`);
      }
      return data.result;
    },
  };
}
